import React, { useState, useEffect, memo } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Snackbar,
  Alert,
  Tooltip,
  Paper,
  Card,
  CardContent,
  Chip,
  Fade,
  Container,
  Grid,
  Button,
  Stack,
  Divider,
  useTheme,
  alpha,
  useMediaQuery
} from "@mui/material";
import { 
  Visibility, 
  VisibilityOff, 
  ContentCopy, 
  People,
  AccountBalanceWallet,
  TrendingUp,
  School,
  Link as LinkIcon,
  CheckCircle,
  AttachMoney
} from "@mui/icons-material";
import { useSnackbar } from '../../../../components/snackbar';

interface ClassroomStatsInterface {
  students: number;
  earnings: number;
  enrollment_url: string;
}

function ClassroomStats({ 
  students,
  earnings, 
  enrollment_url,
}: ClassroomStatsInterface) {
    const { showMessage } = useSnackbar();
    const [copied, setCopied] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

    const [showBalance, setShowBalance] = useState<boolean>(() => {
        const saved = localStorage.getItem('show-classroom-balance');
        return saved !== null ? JSON.parse(saved) : true;
    });


    const toggleBalanceVisibility = () => {
        setShowBalance((prev) => {
            const newValue = !prev;
            localStorage.setItem('show-classroom-balance', JSON.stringify(newValue));
            return newValue;
        });
    };


    const copyEnrollmentUrl = async () => {
        try {
            await navigator.clipboard.writeText(enrollment_url);
            setCopied(true);
            showMessage("Copied to Clipboard", 3);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy URL:', err);
        }
    };


    return (
        <Stack spacing={2}>

            <Box 
                sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: { xs: 2, sm: 2 },
                    p: { xs: 2, sm: 2.5, md: 3 },
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    border: '2px dashed',
                    borderColor: 'grey.300',
                    transition: 'all 0.2s',
                    '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.02)
                    }
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <LinkIcon 
                        sx={{ 
                            color: 'primary.main', 
                            fontSize: 20,
                            alignSelf: { xs: 'flex-start', sm: 'center' },
                            mt: { xs: 0.5, sm: 0 }
                        }}
                    />
                    Enrollment Link 
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 2
                    }}
                >
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            flex: 1,
                            fontFamily: 'monospace',
                            fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
                            wordBreak: 'break-all',
                            color: 'text.primary',
                            bgcolor: 'white',
                            p: { xs: 1.2, sm: 1.5 },
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            lineHeight: 1.4
                        }}
                    >
                        {enrollment_url}
                    </Typography>

                    <Tooltip title={copied ? "Copied!" : "Copy enrollment link"} arrow>
                        <Button
                            variant="contained"
                            onClick={copyEnrollmentUrl}
                            startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                            size={isSmall ? "medium" : "large"}
                            fullWidth={isSmall}
                            sx={{
                            minWidth: { xs: 'auto', sm: 120 },
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: copied ? 'success.main' : 'primary.main',
                            fontSize: { xs: '0.85rem', sm: '0.875rem' },
                            py: { xs: 1, sm: 1.2 },
                            '&:hover': {
                                bgcolor: copied ? 'success.dark' : 'primary.dark',
                                transform: isMobile ? 'none' : 'translateY(-2px)',
                                boxShadow: theme.shadows[isMobile ? 4 : 8]
                            },
                            transition: 'all 0.2s'
                            }}
                        >
                            {copied ? 'Copied!' : 'Copy Link'}
                        </Button>
                    </Tooltip>

                </Box>
                
            </Box>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 4, md: 6 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 4,
                            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                            color: 'white',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'transform 0.2s ease-in-out',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                            },
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: '120px',
                                height: '120px',
                                background: 'rgba(255, 255, 255, 0.08)',
                                borderRadius: '50%',
                                transform: 'translate(40px, -40px)',
                            },
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                width: '80px',
                                height: '80px',
                                background: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '50%',
                                transform: 'translate(-20px, 20px)',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                            <Box>
                                <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5, fontSize: { xs: '2rem', sm: '2.5rem' } }}>
                                    {students}
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                                    Students
                                </Typography>
                            </Box>
                            <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                backdropFilter: 'blur(10px)'
                            }}>
                                <People sx={{ fontSize: 32, opacity: 0.9 }} />
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                <Grid size={{ xs: 12, sm: 4, md: 6 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 4,
                            background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                            color: 'white',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'transform 0.2s ease-in-out',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                            },
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: '120px',
                                height: '120px',
                                background: 'rgba(255, 255, 255, 0.08)',
                                borderRadius: '50%',
                                transform: 'translate(40px, -40px)',
                            },
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                width: '80px',
                                height: '80px',
                                background: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '50%',
                                transform: 'translate(-20px, 20px)',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                                    <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '2rem', sm: '2.5rem' } }}>
                                        {showBalance ? `₦${earnings.toLocaleString()}` : '••••••'}
                                    </Typography>
                                    <IconButton
                                        onClick={toggleBalanceVisibility}
                                        size="small"
                                        sx={{ 
                                            color: 'white', 
                                            opacity: 0.8,
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                opacity: 1,
                                            }
                                        }}
                                    >
                                        {showBalance ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </Box>
                                <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                                    Earnings
                                </Typography>
                            </Box>
                            <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                backdropFilter: 'blur(10px)'
                            }}>
                                <AttachMoney sx={{ fontSize: 32, opacity: 0.9 }} />
                            </Box>
                        </Box>
                    </Paper>
                </Grid>                         
            </Grid>
        </Stack>
    );
}

export default memo(ClassroomStats);